import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { DateTime } from 'luxon'

import { axiosInstance } from '../../../helpers'
import { useTimezone } from '../../../contexts/timezone'
import { KeyboardDatePicker } from '@material-ui/pickers'
import {
    DoubleTransferChain,
    DoubleTransferChainArea,
    DoubleTransferChainBed,
    DoubleTransferChainOccupant,
    DoubleTransferChainResponse,
    DoubleTransferChainWorkOrder,
} from '../../../models'
import { UpdateEzScheduleParams } from '../../../hooks/useEzSchedule/types'

interface Props {
    open: boolean
    schedule?: { id: number }
    onClose: () => void
    alertMode?: boolean
    editReadyDates?: boolean
    destUnitReadyDate?: string | null
    dtUnitReadyDate?: string | null
    minDestUnitReady?: DateTime | null
    minDtUnitReady?: DateTime | null
    maxReadyDate?: DateTime | null
    onUpdateReadyDates?: (params: UpdateEzScheduleParams) => void
}

const useStyles = makeStyles((theme) => ({
    metaRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        color: theme.palette.text.secondary,
        fontSize: 15,
    },
    chainCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
    },
    skippedCard: {
        borderColor: theme.palette.warning.main,
    },
    skipLine: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.75),
        minHeight: 24,
    },
    skipReason: {
        fontSize: 14,
        color: theme.palette.text.secondary,
    },
    chainRow: {
        display: 'flex',
        alignItems: 'stretch',
        gap: theme.spacing(0.5),
    },
    unitBox: {
        flex: 1,
        minWidth: 0,
        border: `1px solid ${theme.palette.text.primary}`,
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
    },
    headerCell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(0.75),
        padding: theme.spacing(0.5, 0.75),
        borderBottom: `1px solid ${theme.palette.text.primary}`,
        minHeight: 34,
    },
    bedTitle: {
        fontSize: 16,
        fontWeight: 700,
        lineHeight: 1.2,
        minWidth: 0,
    },
    statusChip: {
        height: 22,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.3,
    },
    arrowCell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        flexShrink: 0,
        paddingTop: 4,
        alignSelf: 'flex-start',
        height: 34,
        color: theme.palette.text.primary,
    },
    leaseLine: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(0.75),
        padding: theme.spacing(0.5, 0.75),
        fontSize: 15,
        lineHeight: 1.3,
        '& + $leaseLine': {
            borderTop: `1px solid ${theme.palette.divider}`,
        },
    },
    leaseKind: {
        flex: '0 0 28px',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    leaseName: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: 15,
    },
    leaseDate: {
        flex: '0 0 auto',
        fontSize: 14,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    vacant: {
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
    },
    sectionTitle: {
        padding: theme.spacing(0.5, 0.75),
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.action.hover,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    sectionTitleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(0.75),
        padding: theme.spacing(0.5, 0.75),
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.action.hover,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    sectionInfoIcon: {
        fontSize: 16,
        color: theme.palette.text.secondary,
        cursor: 'help',
        flexShrink: 0,
    },
    readyDateTooltip: {
        fontSize: 15,
        lineHeight: 1.4,
        maxWidth: 320,
        padding: theme.spacing(1, 1.25),
    },
    readyNote: {
        padding: theme.spacing(0.75, 0.75, 1),
        fontSize: 14,
        lineHeight: 1.4,
        color: theme.palette.text.secondary,
    },
    workOrderLine: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(0.75),
        padding: theme.spacing(0.5, 0.75),
        fontSize: 15,
        lineHeight: 1.3,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    workOrderService: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: 15,
    },
    dialogContent: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    alertCopy: {
        marginBottom: theme.spacing(1.5),
        fontSize: 15,
        lineHeight: 1.4,
    },
    empty: {
        padding: theme.spacing(2, 0),
        color: theme.palette.text.secondary,
    },
    error: {
        color: theme.palette.error.main,
        padding: theme.spacing(2, 0),
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(3, 0),
    },
}))

const formatDay = (iso: string | null | undefined, timezone: string) => {
    if (!iso) {
        return '—'
    }
    const parsed = DateTime.fromISO(iso, { zone: timezone })
    if (!parsed.isValid) {
        return iso
    }
    return parsed.toFormat('LLL d')
}

const formatBed = (area: DoubleTransferChainArea | null | undefined) => {
    if (!area) {
        return 'Unknown bed'
    }
    return `${area.unit_name} · ${area.area_label}`
}

const OccupantLine = (props: {
    kind: 'Out' | 'In'
    occupant: DoubleTransferChainOccupant | null
    timezone: string
    dateKind: 'move_out' | 'move_in'
}) => {
    const classes = useStyles()
    const { kind, occupant, timezone, dateKind } = props
    const dateValue =
        dateKind === 'move_out' ? occupant?.move_out : occupant?.move_in
    return (
        <Box className={classes.leaseLine}>
            <Typography component="span" className={classes.leaseKind}>
                {kind}
            </Typography>
            {occupant ? (
                <>
                    <Typography component="span" className={classes.leaseName}>
                        {occupant.name || 'Tenant'}
                    </Typography>
                    <Typography component="span" className={classes.leaseDate}>
                        {formatDay(dateValue, timezone)}
                    </Typography>
                </>
            ) : (
                <Typography
                    component="span"
                    className={`${classes.leaseName} ${classes.vacant}`}
                >
                    Vacant
                </Typography>
            )}
        </Box>
    )
}

const ReadyDateBlock = (props: {
    label: string
    iso?: string | null
    timezone: string
    editable?: boolean
    minDate?: DateTime | null
    maxDate?: DateTime | null
    onChange?: (iso: string | null) => void
}) => {
    const classes = useStyles()
    const { label, iso, timezone, editable, minDate, maxDate, onChange } = props
    if (editable && onChange) {
        const value = iso ? DateTime.fromISO(iso).setZone(timezone) : null
        return (
            <Box style={{ padding: '4px 8px 8px' }}>
                <KeyboardDatePicker
                    label={label}
                    fullWidth
                    format="MM/dd/yyyy"
                    placeholder="Select date"
                    value={
                        value?.setZone('local', { keepLocalTime: true }) ?? null
                    }
                    minDate={
                        minDate
                            ?.setZone('local', { keepLocalTime: true })
                            .toJSDate() ?? undefined
                    }
                    maxDate={
                        maxDate
                            ?.setZone('local', { keepLocalTime: true })
                            .toJSDate() ?? undefined
                    }
                    onChange={(date) => {
                        if (date === null) {
                            onChange(null)
                            return
                        }
                        const newDate = DateTime.fromJSDate(date)
                            .setZone(timezone, { keepLocalTime: true })
                            .startOf('day')
                        if (newDate.isValid) {
                            onChange(newDate.toISO())
                        }
                    }}
                />
            </Box>
        )
    }
    return (
        <Box className={classes.workOrderLine}>
            <Typography component="span" className={classes.workOrderService}>
                {label}
            </Typography>
            <Typography component="span" className={classes.leaseDate}>
                {formatDay(iso, timezone)}
            </Typography>
        </Box>
    )
}

const UnitColumn = (props: {
    bed: DoubleTransferChainBed
    timezone: string
    showServices?: boolean
    readyLabel?: string
    readyIso?: string | null
    readyEditable?: boolean
    readyMin?: DateTime | null
    readyMax?: DateTime | null
    onReadyChange?: (iso: string | null) => void
    readyNote?: string
}) => {
    const classes = useStyles()
    const { bed, timezone, showServices = false } = props
    const status = bed.status || bed.area?.status
    return (
        <Box className={classes.unitBox}>
            <Box className={classes.headerCell}>
                <Typography className={classes.bedTitle} noWrap>
                    {formatBed(bed.area)}
                </Typography>
                {status && (
                    <Chip
                        size="small"
                        label={status}
                        className={classes.statusChip}
                    />
                )}
            </Box>
            <Typography className={classes.sectionTitle}>
                Move-in / Move-out
            </Typography>
            <OccupantLine
                kind="Out"
                occupant={bed.outgoing}
                timezone={timezone}
                dateKind="move_out"
            />
            <OccupantLine
                kind="In"
                occupant={bed.incoming}
                timezone={timezone}
                dateKind="move_in"
            />
            {(props.readyLabel || props.readyNote) && (
                <>
                    <Box className={classes.sectionTitleRow}>
                        <span>Unit Ready</span>
                        {props.readyLabel && (
                            <Tooltip
                                title="All work in this bed space will be done prior to the date selected below. You must select a date which gives enough time to complete all the services."
                                classes={{ tooltip: classes.readyDateTooltip }}
                            >
                                <InfoOutlinedIcon
                                    className={classes.sectionInfoIcon}
                                />
                            </Tooltip>
                        )}
                    </Box>
                    {props.readyLabel && (
                        <ReadyDateBlock
                            label={props.readyLabel}
                            iso={props.readyIso}
                            timezone={timezone}
                            editable={props.readyEditable}
                            minDate={props.readyMin}
                            maxDate={props.readyMax}
                            onChange={props.onReadyChange}
                        />
                    )}
                    {props.readyNote && (
                        <Typography className={classes.readyNote}>
                            {props.readyNote}
                        </Typography>
                    )}
                </>
            )}
            {showServices && (
                <>
                    <Typography className={classes.sectionTitle}>
                        Services
                    </Typography>
                    {(bed.workorders ?? []).length === 0 ? (
                        <Box className={classes.workOrderLine}>
                            <Typography
                                component="span"
                                className={`${classes.workOrderService} ${classes.vacant}`}
                            >
                                Not scheduled
                            </Typography>
                        </Box>
                    ) : (
                        (bed.workorders ?? []).map(
                            (
                                workorder: DoubleTransferChainWorkOrder,
                                index,
                            ) => (
                                <Box
                                    className={classes.workOrderLine}
                                    key={`${workorder.service_name}-${workorder.start_date}-${index}`}
                                >
                                    <Typography
                                        component="span"
                                        className={classes.workOrderService}
                                    >
                                        {workorder.service_name}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        className={classes.leaseDate}
                                    >
                                        {formatDay(
                                            workorder.start_date,
                                            timezone,
                                        )}
                                    </Typography>
                                </Box>
                            ),
                        )
                    )}
                </>
            )}
        </Box>
    )
}

const PeopleArrow = () => {
    const classes = useStyles()
    return (
        <Box className={classes.arrowCell}>
            <ArrowBackIcon fontSize="small" />
        </Box>
    )
}

const ChainCard = (props: {
    chain: DoubleTransferChain
    timezone: string
    showServices?: boolean
    destReadyIso?: string | null
    dtReadyIso?: string | null
    destReadyEditable?: boolean
    destReadyMin?: DateTime | null
    dtReadyMin?: DateTime | null
    readyMax?: DateTime | null
    onDestReadyChange?: (iso: string | null) => void
    onDtReadyChange?: (iso: string | null) => void
}) => {
    const classes = useStyles()
    const { chain, timezone } = props
    return (
        <Box
            className={`${classes.chainCard} ${
                chain.skip ? classes.skippedCard : ''
            }`}
        >
            {chain.skip && (
                <Box className={classes.skipLine}>
                    <Chip size="small" color="secondary" label="Skipped" />
                    {chain.skip_label && (
                        <Typography className={classes.skipReason}>
                            {chain.skip_label}
                        </Typography>
                    )}
                </Box>
            )}
            <Box className={classes.chainRow}>
                <UnitColumn
                    bed={chain.dest}
                    timezone={timezone}
                    showServices={props.showServices}
                    readyLabel="Destination unit ready"
                    readyIso={props.destReadyIso}
                    readyEditable={props.destReadyEditable}
                    readyMin={props.destReadyMin}
                    readyMax={props.readyMax}
                    onReadyChange={props.onDestReadyChange}
                />
                <PeopleArrow />
                <UnitColumn
                    bed={chain.dt}
                    timezone={timezone}
                    showServices={props.showServices}
                    readyLabel="Double transfer unit ready"
                    readyIso={props.dtReadyIso}
                    readyEditable={props.destReadyEditable}
                    readyMin={props.dtReadyMin}
                    readyMax={props.readyMax}
                    onReadyChange={props.onDtReadyChange}
                />
                <PeopleArrow />
                <UnitColumn
                    bed={chain.origin}
                    timezone={timezone}
                    showServices={props.showServices}
                    readyNote="This unit will be scheduled in the course of regularly scheduled units."
                />
            </Box>
        </Box>
    )
}

export const DoubleTransferChainModal = (props: Props) => {
    const {
        open,
        schedule,
        onClose,
        alertMode = false,
        editReadyDates = false,
        destUnitReadyDate,
        dtUnitReadyDate,
        minDestUnitReady,
        minDtUnitReady,
        maxReadyDate,
        onUpdateReadyDates,
    } = props
    const classes = useStyles()
    const { currentTimezone } = useTimezone()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [data, setData] = useState<DoubleTransferChainResponse | null>(null)

    useEffect(() => {
        if (!open || !schedule?.id) {
            return
        }
        let cancelled = false
        setLoading(true)
        setError(null)
        axiosInstance
            .get<DoubleTransferChainResponse>(
                `workorder/schedule/${schedule.id}/double-transfer-chains/`,
            )
            .then((response) => {
                if (!cancelled) {
                    setData(response.data)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError('Could not load double transfer chains.')
                    setData(null)
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false)
                }
            })
        return () => {
            cancelled = true
        }
    }, [open, schedule?.id])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="double-transfer-chain-title"
        >
            <DialogTitle id="double-transfer-chain-title">
                Double Transfer Chains
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {alertMode && (
                    <Typography className={classes.alertCopy}>
                        This project has double-transfer (DT) bed spaces. Review
                        each chain and the scheduled services below. Destination
                        units should be ready on the destination ready date; DT
                        units on the double-transfer ready date; origin units
                        last.
                    </Typography>
                )}
                {editReadyDates && (
                    <Typography className={classes.alertCopy}>
                        Select all your services first. Destination unit ready
                        is the day after the last dest service. Double transfer
                        unit ready is the day after the last DT service. Origin
                        units are scheduled last.
                    </Typography>
                )}
                {loading && (
                    <Box className={classes.loading}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {!loading && error && (
                    <Typography className={classes.error}>{error}</Typography>
                )}
                {!loading && !error && data && (
                    <>
                        <Box className={classes.metaRow}>
                            <Typography variant="body2">
                                Work{' '}
                                {formatDay(data.work_start, currentTimezone)} –{' '}
                                {formatDay(data.work_end, currentTimezone)}
                            </Typography>
                            <Typography variant="body2">
                                Transfer{' '}
                                {formatDay(
                                    data.transfer_start,
                                    currentTimezone,
                                )}{' '}
                                –{' '}
                                {formatDay(data.transfer_end, currentTimezone)}
                            </Typography>
                            <Typography variant="body2">
                                Destination unit ready{' '}
                                {formatDay(
                                    destUnitReadyDate ?? data.dest_unit_ready,
                                    currentTimezone,
                                )}
                            </Typography>
                            <Typography variant="body2">
                                Double transfer unit ready{' '}
                                {formatDay(
                                    dtUnitReadyDate ?? data.dt_unit_ready,
                                    currentTimezone,
                                )}
                            </Typography>
                        </Box>
                        {data.chains.length === 0 && (
                            <Typography className={classes.empty}>
                                No double-transfer (DT) beds are tagged on this
                                project.
                            </Typography>
                        )}
                        {data.chains.map((chain) => (
                            <ChainCard
                                key={
                                    chain.dt.area?.area_id ??
                                    `${chain.dest.area?.area_id}-${chain.origin.area?.area_id}`
                                }
                                chain={chain}
                                timezone={currentTimezone}
                                showServices={!editReadyDates}
                                destReadyIso={
                                    destUnitReadyDate ?? data.dest_unit_ready
                                }
                                dtReadyIso={
                                    dtUnitReadyDate ?? data.dt_unit_ready
                                }
                                destReadyEditable={editReadyDates}
                                destReadyMin={minDestUnitReady}
                                dtReadyMin={minDtUnitReady}
                                readyMax={maxReadyDate}
                                onDestReadyChange={(iso) =>
                                    onUpdateReadyDates?.({
                                        dest_unit_ready_date: iso,
                                    })
                                }
                                onDtReadyChange={(iso) =>
                                    onUpdateReadyDates?.({
                                        dt_unit_ready_date: iso,
                                    })
                                }
                            />
                        ))}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    {alertMode ? 'Continue to Turn Board' : 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
